import { useEffect, useMemo } from 'react'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import { useParams } from 'react-router-dom'

import { connect } from 'react-redux'
import { deleteSearchParams, setSearchParamValue } from 'lib/url/searchUrlUtils'
import { fetchPlaceByName } from 'actions/PlaceActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Places from 'constants/places'
import ExperienceSearchPage from '../ExperiencesSearch/ExperienceSearchPage'
import {
  normaliseLocationSlugForLookup,
  resolveCategoryCodesFromSlug,
} from '../../../lib/experiencesListUtils'

type RouteParams = {
  regionCode: string
  locationSlug: string
  categorySlug?: string
}

interface MappedStateProps {
  windowSearch: string
  locationToPlace: App.State['destination']['locationToPlace']
  placesLoading: App.State['destination']['placesLoading']
  categories: App.State['experience']['experienceCategories']
}

type Props = MappedStateProps

function ExperiencesListPage(props: Props) {
  const { locationSlug = '', categorySlug } = useParams<keyof RouteParams>()
  const {
    windowSearch: search,
    categories,
    locationToPlace,
    placesLoading,
  } = props
  const navigation = useAppNavigation()
  const dispatch = useAppDispatch()
  const normalizedSearch = search.startsWith('?') ? search.slice(1) : search
  const searchParams = useMemo(() => new URLSearchParams(normalizedSearch), [normalizedSearch])

  const locationKey = useMemo(() => {
    return normaliseLocationSlugForLookup(locationSlug)
  }, [locationSlug])

  const isAnywhereSlug = locationKey === Places.Anywhere.name.toLowerCase()
  const destinationPlace = isAnywhereSlug ? Places.Anywhere : locationToPlace[locationKey]
  const isFetchingPlace = placesLoading[locationKey]

  useEffect(() => {
    if (!isAnywhereSlug && !destinationPlace && !isFetchingPlace) {
      dispatch(fetchPlaceByName(locationKey))
    }
  }, [dispatch, destinationPlace, isFetchingPlace, isAnywhereSlug, locationKey])

  const resolvedCategoryCodes = useMemo(() => {
    if (!categorySlug) return undefined
    if (!categories?.length) return undefined

    return resolveCategoryCodesFromSlug({
      categorySlug,
      categories,
    })
  }, [categorySlug, categories])

  const nextSearch = useMemo(() => {
    const hasDestinationParams = searchParams.has('destinationId')
    const hasExperienceParams = searchParams.has('experienceId')
    const hasCategoryParams = searchParams.has('categories')

    if (!destinationPlace || !('id' in destinationPlace) || !destinationPlace.id) {
      if (hasDestinationParams) {
        return normalizedSearch
      }
      return deleteSearchParams(normalizedSearch, 'destinationId', 'destinationName', 'categories', 'experienceId', 'experienceName')
    }

    let updatedSearch = normalizedSearch

    if (!hasDestinationParams) {
      updatedSearch = setSearchParamValue(updatedSearch, 'destinationId', destinationPlace.id)
      updatedSearch = setSearchParamValue(updatedSearch, 'destinationName', destinationPlace.name)
    }

    if (!hasExperienceParams) {
      updatedSearch = deleteSearchParams(updatedSearch, 'experienceId', 'experienceName')
    }

    if (!hasCategoryParams) {
      if (resolvedCategoryCodes?.length) {
        updatedSearch = setSearchParamValue(updatedSearch, 'categories', resolvedCategoryCodes.join(','))
        return updatedSearch
      }

      return deleteSearchParams(updatedSearch, 'categories')
    }

    return updatedSearch
  }, [destinationPlace, normalizedSearch, resolvedCategoryCodes, searchParams])

  useEffect(() => {
    if (nextSearch !== normalizedSearch) {
      navigation.replace({
        search: nextSearch ? `?${nextSearch}` : '',
      })
    }
  }, [navigation, nextSearch, normalizedSearch])
  if (nextSearch !== normalizedSearch) {
    return <LoadingIndicator />
  }
  if (!destinationPlace && isFetchingPlace) {
    return <LoadingIndicator />
  }
  return <ExperienceSearchPage />
}

function mapStateToProps(state: App.State): MappedStateProps {
  return {
    windowSearch: state.router.location.search,
    locationToPlace: state.destination.locationToPlace,
    placesLoading: state.destination.placesLoading,
    categories: state.experience.experienceCategories,
  }
}

export default connect(mapStateToProps)(ExperiencesListPage)
